import DOMPurify from 'dompurify';

/**
 * Security utility for sanitizing HTML content to prevent XSS attacks
 */

// Configure DOMPurify with secure defaults
const SANITIZE_CONFIG = {
  // Allow common HTML tags that are safe for content display
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'img', 'div', 'span',
    'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr'
  ],
  // Allow safe attributes
  ALLOWED_ATTR: [
    'href', 'target', 'alt', 'src', 'width', 'height', 'class', 'id',
    'title', 'data-*', 'style'
  ],
  // Allow safe URL schemes
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // Additional security options
  FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  SANITIZE_DOM: true,
  FORCE_BODY: false,
  SANITIZE_NAMED_PROPS: true,
  CUSTOM_ELEMENT_HANDLING: {
    tagNameCheck: null,
    attributeNameCheck: null,
    allowCustomizedBuiltInElements: false,
  },
};

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting
 * @param content - Raw HTML content to sanitize
 * @param config - Optional custom DOMPurify configuration
 * @returns Sanitized HTML content safe for rendering
 */
export function sanitizeHtml(
  content: string, 
  config: Partial<typeof SANITIZE_CONFIG> = {}
): string {
  if (!content || typeof content !== 'string') {
    return '';
  }

  // Merge custom config with defaults
  const finalConfig = { ...SANITIZE_CONFIG, ...config };

  try {
    // Sanitize the content using DOMPurify
    const sanitized = DOMPurify.sanitize(content, finalConfig);
    
    // Additional safety check - ensure result is a string
    return typeof sanitized === 'string' ? sanitized : '';
  } catch (error) {
    console.error('HTML sanitization failed:', error);
    // Return empty string on sanitization failure for security
    return '';
  }
}

/**
 * Sanitizes HTML content for blog posts with enhanced security
 * @param content - Blog post HTML content
 * @returns Sanitized HTML content
 */
export function sanitizeBlogContent(content: string): string {
  return sanitizeHtml(content, {
    // More restrictive config for blog content
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'img', 'div'
    ],
    ALLOWED_ATTR: ['href', 'target', 'alt', 'src', 'width', 'height', 'class'],
  });
}

/**
 * Sanitizes user-generated content with stricter rules
 * @param content - User input HTML content
 * @returns Sanitized HTML content
 */
export function sanitizeUserContent(content: string): string {
  return sanitizeHtml(content, {
    // Very restrictive for user content
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u'],
    ALLOWED_ATTR: [],
  });
}

/**
 * Validates if content contains potentially dangerous elements
 * @param content - HTML content to validate
 * @returns true if content appears safe, false if suspicious
 */
export function validateContentSafety(content: string): boolean {
  if (!content || typeof content !== 'string') {
    return true;
  }

  // Check for suspicious patterns
  const dangerousPatterns = [
    /<script[^>]*>/i,
    /javascript:/i,
    /vbscript:/i,
    /data:text\/html/i,
    /on\w+\s*=/i, // Event handlers like onclick, onload, etc.
    /<iframe[^>]*>/i,
    /<object[^>]*>/i,
    /<embed[^>]*>/i,
    /<form[^>]*>/i,
  ];

  return !dangerousPatterns.some(pattern => pattern.test(content));
}